import { useState } from 'react'
import Modal from '../ui/Modal.jsx'
import { apiFetch } from '../../lib/api.js'
import SafeHtml from '../ui/SafeHtml.jsx'

const DIFF_COLORS_DARK = {
  facile:    'bg-blue-500/20 text-blue-300',
  moyen:     'bg-amber-500/20 text-amber-300',
  difficile: 'bg-red-500/20 text-red-300',
}

export default function GenerateModal({ open, deckId, onClose, onImported, formTitle }) {
  const [step, setStep] = useState('form') // 'form' | 'loading' | 'preview'
  const [form, setForm] = useState({ description: '', nb_sous_themes: 3, nb_questions_par_sous_theme: 5 })
  const [preview, setPreview] = useState(null)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState('')
  const [expandedIndex, setExpandedIndex] = useState(null)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  function handleClose() {
    if (step === 'loading') return // pas de fermeture pendant la génération
    setStep('form')
    setPreview(null)
    setError('')
    setExpandedIndex(null)
    onClose()
  }

  async function handleGenerate(e) {
    e.preventDefault()
    setError('')
    setStep('loading')
    try {
      const result = await apiFetch(`/api/decks/${deckId}/generate`, {
        method: 'POST',
        body: JSON.stringify(form),
      })
      setPreview(result)
      setExpandedIndex(null)
      setStep('preview')
    } catch (e) {
      setError(e.message)
      setStep('preview')
    }
  }

  async function handleImport() {
    setImporting(true)
    setError('')
    try {
      await apiFetch(`/api/decks/${deckId}/generate/import`, {
        method: 'POST',
        body: JSON.stringify(preview),
      })
      onImported()
      handleClose()
    } catch (e) {
      setError(e.message)
    }
    setImporting(false)
  }

  const nbQuestions = preview?.questions?.length ?? 0
  const nbSousThemes = preview?.sous_themes?.length ?? 0

  const baseFormTitle = formTitle ?? 'Compléter avec l\'IA'
  const modalTitle = step === 'preview' && !error
    ? `Aperçu — ${nbSousThemes} sous-thème${nbSousThemes > 1 ? 's' : ''} · ${nbQuestions} question${nbQuestions > 1 ? 's' : ''}`
    : baseFormTitle

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={modalTitle}
      maxWidth="max-w-2xl"
    >
      {/* Étape 1 — Formulaire */}
      {step === 'form' && (
        <form onSubmit={handleGenerate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">
              Description du sujet
            </label>
            <textarea
              required
              rows={4}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="ex. Révision sur la Révolution française pour le bac de terminale"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Nombre de sous-thèmes
              </label>
              <input
                type="number"
                min={1}
                max={8}
                value={form.nb_sous_themes}
                onChange={e => set('nb_sous_themes', parseInt(e.target.value) || 3)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ colorScheme: 'dark' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Questions par sous-thème
              </label>
              <input
                type="number"
                min={1}
                max={30}
                value={form.nb_questions_par_sous_theme}
                onChange={e => set('nb_questions_par_sous_theme', parseInt(e.target.value) || 5)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ colorScheme: 'dark' }}
              />
            </div>
          </div>

          <div className="bg-blue-500/10 border border-blue-400/30 text-blue-300 rounded-lg px-4 py-3 text-sm">
            La génération peut prendre 15 à 30 secondes selon la longueur du contenu demandé.
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={handleClose} className="px-4 py-2 text-white/60 hover:text-white transition">
              Annuler
            </button>
            <button type="submit" className="btn-primary px-6 py-2">
              Générer
            </button>
          </div>
        </form>
      )}

      {/* Étape 2 — Chargement */}
      {step === 'loading' && (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <svg className="animate-spin h-10 w-10 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <p className="text-lg font-medium text-white">Génération en cours…</p>
          <p className="text-sm text-white/50">Cela peut prendre 15 à 30 secondes</p>
        </div>
      )}

      {/* Étape 3 — Aperçu */}
      {step === 'preview' && (
        <div className="space-y-4">
          {error ? (
            <>
              <div className="bg-red-500/15 border border-red-500/30 text-red-300 rounded-lg px-4 py-3">
                {error}
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={handleClose} className="px-4 py-2 text-white/60 hover:text-white transition">
                  Annuler
                </button>
                <button
                  onClick={() => { setError(''); setStep('form') }}
                  className="btn-primary px-6 py-2"
                >
                  Réessayer
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Accordéon sous-thèmes */}
              <div className="space-y-2">
                {preview.sous_themes.map((st, stIdx) => {
                  const questionsTheme = preview.questions.filter(q => q.sous_theme_index === stIdx)
                  const isOpen = expandedIndex === stIdx
                  return (
                    <div key={stIdx} className="border border-white/10 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setExpandedIndex(isOpen ? null : stIdx)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 transition text-left"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-semibold shrink-0">
                            {stIdx + 1}
                          </span>
                          <span className="font-medium text-white">{st.nom}</span>
                          <span className="text-xs text-white/40">{questionsTheme.length} question{questionsTheme.length > 1 ? 's' : ''}</span>
                        </div>
                        <svg
                          className={`w-4 h-4 text-white/40 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {isOpen && (
                        <div className="divide-y divide-white/10">
                          {questionsTheme.map((q, qIdx) => (
                            <div key={qIdx} className="px-4 py-3">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${DIFF_COLORS_DARK[q.difficulte] ?? 'bg-white/10 text-white/60'}`}>
                                  {q.difficulte}
                                </span>
                                <p className="text-sm font-medium text-white"><SafeHtml html={q.enonce} /></p>
                              </div>
                              <div className="grid grid-cols-2 gap-1">
                                {q.reponses.map((r, i) => (
                                  <div
                                    key={i}
                                    className={`text-xs px-2 py-1 rounded ${
                                      q.bonne_reponse === i
                                        ? 'bg-green-500/20 text-green-300 font-medium'
                                        : 'bg-white/5 text-white/50'
                                    }`}
                                  >
                                    {String.fromCharCode(65 + i)}. {r}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Footer */}
              <div className="flex gap-3 justify-end pt-2">
                <button onClick={handleClose} className="px-4 py-2 text-white/60 hover:text-white transition">
                  Annuler
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing}
                  className="btn-primary px-6 py-2 disabled:opacity-60"
                >
                  {importing ? 'Import en cours…' : `Importer tout (${nbQuestions} questions)`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  )
}
